import React from 'react'
import moment from 'moment'
import isArray from 'lodash/isArray'
import includes from 'lodash/includes'
import without from 'lodash/without'
import get from 'lodash/get'
import Wysiwyg from '../wysiwyg'
import DateTimePicker from '../date-time-picker'
import CountryState from '../country-state'

import {
  TextField,
  Toggle,
  AutoComplete,
  DatePicker,
  SelectField,
  MenuItem,
  RaisedButton,
  FlatButton
} from 'material-ui'

import ChipInput from 'material-ui-chip-input'
import Repeater from './repeater'

const styles = {
  currentImage: {
    maxWidth: '300px',
    display: 'block'
  }
}

const getFieldContainerStyle = ({ fields, inline }) => {
  if (inline) {
    return {
      width: `${Math.floor(100 / fields.length) - 2}%`,
      marginRight: '10px',
      float: 'left'
    }
  }
}

const FieldList = (props) => {
  const fieldContainerStyle = getFieldContainerStyle(props)

  return (
    <div>
      {props.fields.map((field, i) => {
        if (Array.isArray(field)) {
          return <FieldList key={i} {...props} fields={field} inline />
        } else if (field.group) {
          return (
            <div key={i} style={{ ...fieldContainerStyle, marginTop: '1em' }}>
              <label>{field.name}</label>
              <FieldList {...props} fields={field.fields} />
            </div>
          )
        }

        return (
          <div key={i} style={fieldContainerStyle}>
            <Field {...props} field={field} />
            {field.helperText && (
              <div dangerouslySetInnerHTML={{ __html: field.helperText }} />
            )}
          </div>
        )
      })}
    </div>
  )
}

const Field = ({ field, ...props }) => {
  if (field.showIf && !field.showIf(props.resource)) {
    return <div key={field.name} />
  }

  const errorText = get(props.errors, field.prop, []).join(', ')

  switch (field.type) {
    case 'text':
    case 'textarea':
    case 'password':
    case 'number':
      const handleTextChange = (e, value) => {
        field.onChange && field.onChange(value, props.resource)
        props.onChange(field, e, value)
      }

      const isEditing = props.resource && 'id' in props.resource
      const inputProperties = {
        key: field.name,
        type: field.type,
        disabled: field.disabled,
        floatingLabelText: field.name,
        value:
          props.resource[field.prop] || (!isEditing && field.default) || '',
        errorText,
        onChange: handleTextChange,
        multiLine: field.multiline,
        rows: field.rows,
        rowsMax: field.rowsMax
      }

      if (field.type === 'number') {
        if ('min' in field) {
// @ts-ignore
          inputProperties.min = field.min
        }

        if ('max' in field) {
// @ts-ignore
          inputProperties.max = field.max
        }
      } else if (field.type === 'textarea') {
        inputProperties.multiLine = true
        inputProperties.rows = 2
        inputProperties.rowsMax = 4
      } else if (field.type === 'password') {
        // fix for password autocomplete - https://stackoverflow.com/a/36584903
// @ts-ignore
        inputProperties.autoComplete = 'new-password'
      }

      return <TextField {...inputProperties} fullWidth />
    case 'wysiwyg':
      return (
        <Wysiwyg
          theme='snow'
          label={field.name}
          key={field.name}
          value={props.resource[field.prop] || ''}
          onChange={props.onChange.bind(null, field, undefined)}
          errorText={errorText}
        />
      )
    case 'date':
      const date = props.resource[field.prop]
        ? moment(props.resource[field.prop]).toDate()
        : null

      const handleDateChange = (e, value) => {
        if (field.filter) {
          value = field.filter(value, props.resource)
        }
        props.onChange(field, e, value)
      }

      return (
        <DatePicker
          firstDayOfWeek={0}
          key={field.name}
          floatingLabelText={field.name}
          value={date}
          autoOk
          errorText={errorText}
          onChange={handleDateChange}
          disabled={field.disabled || false}
        />
      )
    case 'datetime':
      const handleDateTimeChange = (e, value) => {
        field.onChange && field.onChange(value, props.resource)
        props.onChange(field, e, value)
      }
      return (
        <DateTimePicker
          name={field.name}
          key={field.name}
          value={props.resource[field.prop] || null}
          errorText={errorText}
          onChange={handleDateTimeChange}
          disabled={field.disabled || false}
        />
      )
    case 'checkbox':
      let handleToggle, isToggled

      if (field.isArray) {
        isToggled = includes(props.resource[field.prop], field.item)

        handleToggle = (event, isChecked) => {
          let newValue = isArray(props.resource[field.prop])
            ? props.resource[field.prop]
            : []

          if (isChecked && !includes(newValue, field.item)) {
            newValue.push(field.item)
          } else if (!isChecked && includes(newValue, field.item)) {
            newValue = without(newValue, field.item)
          }

          props.onChange(field, event, newValue)
        }
      } else {
        isToggled = props.resource[field.prop] || false
        handleToggle = props.onChange.bind(null, field)
      }
      const handleToggleChange = (e, isChecked) => {
        field.onChange && field.onChange(isChecked, props.resource)
        handleToggle(e, isChecked)
      }

      return (
        <Toggle
          key={field.name}
          label={field.name}
          labelPosition='right'
          onToggle={handleToggleChange}
          disabled={field.disabled || false}
          toggled={isToggled}
          style={{ marginTop: '1em' }}
        />
      )
    case 'select':
      const handleSelectChange = (event, index, value) => {
        field.onChange && field.onChange(value, props.resource)
        props.onChange(field, event, value, index)
      }

      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <SelectField
            key={field.name}
            floatingLabelText={field.name}
            onChange={handleSelectChange}
            errorText={errorText}
            value={props.resource[field.prop] || ''}
          >
            {field.options.map((option) => {
              return (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  primaryText={option.name}
                  rightIcon={option.icon || null}
                />
              )
            })}
          </SelectField>
          {field.isClearable && props.resource[field.prop] && (
            <FlatButton
              primary
              label='Clear'
              onClick={() => handleSelectChange(null, null, '')}
            />
          )}
        </div>
      )
    case 'country-state':
      return (
        <CountryState
          key={field.name}
          onCountryChange={(value) =>
            props.onChange({ prop: field.countryProp }, null, value)
          }
          onStateChange={(value) =>
            props.onChange({ prop: field.stateProp }, null, value)
          }
          countryErrorText={props.errors[field.countryProp]}
          stateErrorText={props.errors[field.stateProp]}
          countryValue={props.resource[field.countryProp] || ''}
          stateValue={props.resource[field.stateProp] || ''}
        />
      )
    case 'autocomplete':
      const handleAutocompleteSelection = (chosenRequest, selectedIndex) => {
        field.onChange && field.onChange(chosenRequest, props.resource)
        props.handleAutocompleteSelection(field, chosenRequest, selectedIndex)
      }

      const autocompletePath = [field.references, field.prop]
      const dataSource = get(props.autocompleteResults, autocompletePath, [])
      const searchText = get(
        props.autocompleteText,
        autocompletePath,
        undefined
      )

      return (
        <AutoComplete
          disabled={field.disabled}
          fullWidth
          openOnFocus
          onFocus={props.handleAutocompleteFocus.bind(null, field)}
          key={field.name}
          floatingLabelText={`${field.name} ${props.resource[field.prop] ? '#' + props.resource[field.prop] : ''
          }`}
          dataSource={dataSource}
          dataSourceConfig={{
            text: field.displayProp || field.referenceProp,
            value: field.referenceValue || 'id'
          }}
          onNewRequest={handleAutocompleteSelection}
          filter={() => true}
          errorText={errorText}
          searchText={searchText}
          onUpdateInput={props.handleAutocompleteUpdateInput.bind(null, field)}
          style={{ marginRight: '1em' }}
          listStyle={{ maxHeight: '300px', overflow: 'auto' }}
          onClose={(e) => {
            if (!props.resource[field.prop] && field.rejectCustomValues) {
              props.handleAutocompleteUpdateInput(field, '')
            }
          }}
        />
      )
    case 'chipinput':
      const keyCodes = { tab: 9, space: 32, enter: 13, comma: 188 }
      return (
        <ChipInput
          key={field.name}
          floatingLabelText={field.name}
          onRequestAdd={props.handleChipInputAdd.bind(null, field)}
          onRequestDelete={props.handleChipInputDelete.bind(null, field)}
          value={props.resource[field.prop] || []}
          style={{ marginRight: '1em' }}
          errorText={errorText}
          fullWidth
          newChipKeyCodes={Object.values(keyCodes)}
        />
      )
    case 'file':
      const resourceFile = props.resourceFiles[field.prop]
      const fileUrl = props.resource[field.url]
      const filename = resourceFile ? resourceFile.file.name : ''

      return (
        <div key={field.name} style={{ paddingBottom: '15px' }}>
          <RaisedButton
            containerElement='label'
            label={`Upload ${field.name}`}
            style={{ marginTop: '1em' }}
          >
            <input
              type='file'
              style={{ display: 'none' }}
              onChange={props.handleFileChange.bind(null, field)}
            />
          </RaisedButton>
          {fileUrl && (
            <p>
              Current File:{' '}
              <a href={fileUrl} target='_blank' rel='noreferrer'>
                {fileUrl}
              </a>
            </p>
          )}
          {filename && <p>New File: {filename}</p>}
        </div>
      )
    case 'image':
      const imageFile = props.resourceFiles[field.prop]
      const imageUrl = props.resource[field.url]
      const imagename = imageFile ? imageFile.file.name : ''

      return (
        <div key={field.name} style={{ paddingBottom: '15px' }}>
          <RaisedButton
            containerElement='label'
            label={`Upload ${field.name}`}
            style={{ marginTop: '1em' }}
          >
            <input
              accept='image/*'
              type='file'
              style={{ display: 'none' }}
              onChange={props.handleFileChange.bind(null, field)}
            />
          </RaisedButton>
          {imageUrl && (
            <p>
              Current Image: <img src={imageUrl} style={styles.currentImage} />
            </p>
          )}
          {imagename && <p>New Image: {imagename}</p>}
        </div>
      )
    case 'repeater':
      return (
        <Repeater
          label={field.name}
          value={props.resource[field.prop]}
          newValue={field.newValue}
          onChange={props.onChange.bind(null, field)}
          resource={props.resource}
          component={field.component}
        />
      )
    case 'component':
      const { componentProps = {} } = field
      return (
        <field.component
          label={field.name}
          key={field.prop}
          value={props.resource[field.prop]}
          onChange={props.onChange.bind(null, field)}
          errorText={errorText}
          resource={props.resource}
          {...componentProps}
        />
      )
  }
}

export default FieldList
